import "./style.css";
import GenericStatCard from "../../../Components/Cards/GenericStatCard";
import GenericTable from "../../../Components/Tables/GenericTable";
import garageMoneyIcon from "../../../Assets/Icons/garage_money.svg";
import laptopCarIcon from "../../../Assets/Icons/laptop_car.svg";
import creditCardICon from "../../../Assets/Icons/credit_card_gear.svg";
import { useNavigate } from "react-router-dom";
import { Dropdown, Modal, notification } from "antd";
import { EyeOutlined, LockOutlined } from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import LineChart from "../../../Components/Charts/LineChart";
import { useState, useEffect } from "react";
import TopUpCard from "./TopUpCard";
import GenericModal from "../../../Components/Modals/GenericModal";
import { getAdminCredits } from "../../../API/Admin/CreditsBillings";
import { updateFleetCompanyFuelingStatus } from "../../../API/Admin/FleetManagement";
import usePageAccess from "../../../Utlis/usePageAccess";
import { getInitials } from "../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BADGE_COLORS = [
  { background: "#4f46e51f", color: "#4f46e5" },
  { background: "#fee2e2", color: "#dc2626" },
  { background: "#ecfdf5", color: "#059669" },
  { background: "#ede9fe", color: "#7c3aed" },
  { background: "#f59e0b1f", color: "#f59e0b" },
  { background: "#dbeafe", color: "#3b82f6" },
  { background: "#ea580c1a", color: "#ea580c" },
  { background: "#fdf2f8", color: "#db2777" },
  { background: "#f0fdfa", color: "#0d9488" },
];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const getModuleBadgeStyle = (name) => {
  if (!name || name === "-") {
    return { background: "#f4f4f5", color: "#71717a" };
  }
  if (name.toLowerCase() === "fuel") {
    return { background: "#0000000f", color: "#000000" };
  }
  return BADGE_COLORS[hashString(name) % BADGE_COLORS.length];
};

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const ImageWithFallback = ({ src, name, size = 24, radius = "5px" }) =>
  src ? (
    <img
      src={src}
      alt={name}
      style={{ width: size, height: size, borderRadius: radius, objectFit: "cover" }}
    />
  ) : (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 700,
        color: "#fff",
        background: getAvatarColor(name),
        flexShrink: 0,
      }}
    >
      {getInitials(name)}
    </span>
  );

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "-");
};

const formatTime = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const CreditsBillings = () => {
  const { canEdit } = usePageAccess("credits-billing");
  const navigate = useNavigate();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [adminCredits, setAdminCredits] = useState(null);

  const fetchAdminCredits = async () => {
    try {
      const res = await getAdminCredits();

      if (Math.floor(res?.status / 100) === 2) {
        setAdminCredits(res?.data?.data || res?.data || null);
      }
    } catch (err) {
      console.error("Failed to load admin credits:", err);
    }
  };

  useEffect(() => {
    fetchAdminCredits();
  }, []);

  const creditTopUps = (adminCredits?.top_ups || []).map((topUp) => ({
    key: topUp.id,
    company: topUp.legal_name || "-",
    method: topUp.payment_method || "-",
    amount: parseFloat(topUp.amount) || 0,
    date: formatDate(topUp.payment_date),
    time: formatTime(topUp.created_at),
    logo: resolveAvatarUrl(topUp.company_avatar),
  }));

  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  const handleView = (record) => {
    navigate("/fleet-credit-details", {
      state: { companyId: record.companyId },
    });
  };

  const handleManageCredits = (record) => {
    navigate("/credit-adjustments", {
      state: { companyId: record.companyId },
    });
  };

  const handleLockFueling = (record) => {
    setSelectedRow(record);
    setIsConfirmOpen(true);
  };

  const dataSource = (adminCredits?.companies || [])
    .filter((company) => !company.fueling_locked)
    .map((company) => ({
    key: String(company.id),
    companyId: company.id,
    company: company.legal_name || "-",
    vehicles: company.active_vehicle_count ?? 0,
    drivers: company.active_driver_count ?? 0,
    amount: formatCurrency(company.balance),
    status: company.fueling_locked ? "inactive" : "active",
    fuelingLocked: company.fueling_locked,
    logo: resolveAvatarUrl(company.avatar),
    assignModule: Array.isArray(company.assigned_modules)
      ? company.assigned_modules
      : [],
  }));

  const statusFilters = [
    ...new Set(dataSource.map((item) => item.status)),
  ].map((status) => ({
    text: status.charAt(0).toUpperCase() + status.slice(1),
    value: status,
  }));

  const moduleFilters = [
    ...new Set(dataSource.flatMap((item) => item.assignModule)),
  ].map((module) => ({
    text: module,
    value: module,
  }));

  const columns = [
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      enableSearch: "true",
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ImageWithFallback src={record.logo} name={text} />
          {text}
        </span>
      ),
    },
    {
      title: "Assign Module",
      dataIndex: "assignModule",
      key: "assignModule",
      filters: moduleFilters,
      onFilter: (value, record) => record.assignModule?.includes(value),
      render: (modules) =>
        Array.isArray(modules) && modules.length > 0 ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {modules.map((module) => (
              <span
                key={module}
                className="assigned-module-badge"
                style={getModuleBadgeStyle(module)}
              >
                {module}
              </span>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Vehicles",
      dataIndex: "vehicles",
      key: "vehicles",
      sorter: (a, b) => (a.vehicles || 0) - (b.vehicles || 0),
    },
    {
      title: "Drivers",
      dataIndex: "drivers",
      key: "drivers",
      sorter: (a, b) => (a.drivers || 0) - (b.drivers || 0),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) =>
        parseFloat(a.amount.replace(/[^0-9.-]+/g, "")) -
        parseFloat(b.amount.replace(/[^0-9.-]+/g, "")),
      render: (text) => <span className="creidt-amount">{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${text === "active" ? "approved" : "denied"}`}
        >
          {text.charAt(0).toUpperCase() + text.slice(1)}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          canEdit && {
            key: "manageCredits",
            label: "Manage Credits",
            style: { color: "#000000", fontWeight: 500 },
            icon: <img src={creditCardICon} alt="Icon" />,
            onClick: () => handleManageCredits(record),
          },
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            style: { color: "#000000", fontWeight: 500 },
            onClick: () => handleView(record),
          },
          canEdit && {
            key: "lockFueling",
            label: "Lock Fueling",
            icon: <LockOutlined />,
            style: { color: "#DC2626", fontWeight: 500 },
            onClick: () => handleLockFueling(record),
          },
        ].filter(Boolean);
        return (
          <>
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: 24,
                  height: 24,
                }}
              >
                <BsThreeDotsVertical
                  style={{ fontSize: 20, color: "#4F46E5" }}
                />
              </span>
            </Dropdown>
          </>
        );
      },
    },
  ];

  const handleConfirmLock = async (enteredReason) => {
    if (!selectedRow?.companyId) return;

    try {
      const res = await updateFleetCompanyFuelingStatus(
        selectedRow.companyId,
        { fueling_locked: true, reason: enteredReason },
      );

      if (Math.floor(res?.status / 100) === 2) {
        setIsConfirmOpen(false);
        fetchAdminCredits();

        notification.success({
          message: "Fuel Locked Successfully",
          placement: "topRight",
          icon: <LockOutlined style={{ color: "#16A34A" }} />,
        });
      } else {
        notification.error({
          message: "Action Failed",
          description: res?.data?.message || "Failed to lock fueling.",
          placement: "topRight",
        });
      }
    } catch (error) {
      notification.error({
        message: "Action Failed",
        description:
          error?.response?.data?.message ||
          "Unable to lock fueling at this time. Please try again.",
        placement: "topRight",
      });
    }
  };
  return (
    <div className="credits-billings-page">
      {/* Stats Cards */}
      <div className="row">
        <div className="stats-container">
          <GenericStatCard
            title="Total Credit balance"
            value={formatCurrency(adminCredits?.total_balance?.value)}
            {...(adminCredits?.total_balance?.percentage != null && {
              percentage: `${adminCredits.total_balance.percentage}`,
            })}
            isIncrease={adminCredits?.total_balance?.is_increase}
            icon={garageMoneyIcon}
          />
          <GenericStatCard
            title="Total Active Fleet"
            value={adminCredits?.active_fleets?.value ?? 0}
            {...(adminCredits?.active_fleets?.percentage != null && {
              percentage: `${adminCredits.active_fleets.percentage}`,
            })}
            isIncrease={adminCredits?.active_fleets?.is_increase}
            icon={laptopCarIcon}
          />
        </div>
      </div>

      <div className="row">
        <div className="graph">
          <LineChart
            graphData={{
              labels: adminCredits?.credit_balance_trend?.labels || [],
              total_balance: (adminCredits?.credit_balance_trend?.total_balance || []).map(
                (v) => parseFloat(v) || 0
              ),
            }}
            lengthKeys={1}
            colourTheme={["#16A34A"]}
            graphTitle="Credit Balance"
            selectTenByDefault={false}
            units={["$"]}
            classNameCSS="credit-balance"
          />
        </div>

        <TopUpCard
          creditTopUps={creditTopUps}
          showModal={showModal}
          limit={5}
        />
      </div>

      {/* CreditsBillings Table */}
      <div>
        <GenericTable
          title="Fleet Credit Balance"
          dataSource={dataSource}
          columns={columns}
          enableRowSelection={false}
          onSelectionChange={(keys, rows) => console.log(keys, rows)}
        />
        <GenericModal
          open={isConfirmOpen}
          title="Confirm Fueling Lock"
          subtitle={`Are you sure you want to lock fueling of ${selectedRow?.company}? This will immediately decline all active card transactions.`}
          highlightText={selectedRow?.company}
          textareaLabel="Reason"
          textareaPlaceholder="Enter reason..."
          confirmText="Confirm Lock"
          cancelText="Cancel"
          iconColor="#ef4444"
          iconBgColor="#ef44441a"
          confirmBtnColor="#EA580C"
          onCancel={() => setIsConfirmOpen(false)}
          onConfirm={handleConfirmLock}
        />
      </div>

      <Modal
        title="All Credit Top-Ups"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
        styles={{
          header: { padding: "16px 20px" },
          body: { maxHeight: "75vh", overflowY: "auto" },
        }}
      >
        <TopUpCard creditTopUps={creditTopUps} showModal={showModal} />
      </Modal>
    </div>
  );
};

export default CreditsBillings;
